/**
 * 团游订单接口（对接 group-tour-order）
 */
import { http } from "@/http/axios";
import type {
  TourOrderListParams,
  TourOrderListResponse,
  TourOrderDetailResponse,
  CancelOrderParams,
  CancelOrderResponse,
} from "@/api/tourOrder";
import type { RefundInfo } from "@/api/order";

// ==================== 请求 ====================

/** 预创建订单请求参数 */
export interface PreCreateOrderParams {
  group_tour_id: number;
}

/** 确认订单 - 出行人 */
export interface ConfirmGroupTraveler {
  name: string;
  type_name: string; // 成人 / 儿童 / 老人 / 幼儿
  phone?: string;
  id_card?: string;
}

/** 确认订单请求参数 */
export interface ConfirmGroupOrderParams {
  group_tour_id: number;
  start_date: string;
  end_date: string;
  room_type: string;
  room_count: number;
  single_room_count: number;
  single_bike_count: number;
  contact_name: string;
  contact_phone: string;
  contact_email?: string;
  travelers: ConfirmGroupTraveler[];
  special_requests?: string[];
}

// ==================== 响应 ====================

/** 产品摘要 */
export interface PreCreateProduct {
  id: number;
  title: string;
  product_no: string;
  attractions: string;
  cover_image: string;
  days: number;
  nights: number;
  departure_city: string;
  destinations: string;
  fee_include: string[];
  group_people: number;
  images: string[];
  intro: string;
  stock: number;
}

/** 预创建返回的档位 */
export interface PreCreateRoomType {
  room_type: string;
  price: number;                 // 成本价
  sale_price: number;            // 销售价
  agent_rate: number;            // 代理费率
  single_room_supplement: number; // 单房差
  single_bike_seat: number;      // 单人单车位
}

/** 预创建返回的日期段 */
export interface PreCreatePeriod {
  start_date: string;
  end_date: string;
  room_types: PreCreateRoomType[];
}

/** 预创建订单响应 data */
export interface PreCreateOrderData {
  product: PreCreateProduct;
  periods: PreCreatePeriod[];
}

/** 预创建订单响应 */
export interface PreCreateOrderResponse {
  code: number;
  message: string;
  data: PreCreateOrderData;
}

/** 确认订单响应 data（占位，拿到示例后替换） */
// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface ConfirmGroupOrderData {
  [key: string]: unknown;
}

/** 确认订单响应 */
export interface ConfirmGroupOrderResponse {
  code: number;
  message: string;
  data: ConfirmGroupOrderData;
}

// ==================== 接口请求 ====================

/**
 * 预创建团游订单
 * POST /home/group-tour-order/pre-create
 */
export function preCreateGroupTourOrder(params: PreCreateOrderParams) {
  return http.post<PreCreateOrderResponse>("/home/group-tour-order/pre-create", params);
}

/**
 * 确认团游订单
 * POST /home/group-tour-order/confirm
 */
export function confirmGroupTourOrder(params: ConfirmGroupOrderParams) {
  return http.post<ConfirmGroupOrderResponse>("/home/group-tour-order/confirm", params);
}

/**
 * 获取团游订单列表
 * GET /home/group-tour-order/list
 */
export function getGroupTourOrderList(params: TourOrderListParams = {}) {
  return http.get<TourOrderListResponse>("/home/group-tour-order/list", { params });
}

/**
 * 获取团游订单详情
 * GET /home/group-tour-order/detail
 */
export function getGroupTourOrderDetail(id: number) {
  return http.get<TourOrderDetailResponse>("/home/group-tour-order/detail", {
    params: { id },
  });
}

/**
 * 取消团游订单
 * POST /home/group-tour-order/cancel
 */
export function cancelGroupTourOrder(params: CancelOrderParams) {
  return http.post<CancelOrderResponse>("/home/group-tour-order/cancel", params);
}

// ==================== 售后接口 ====================

/** 发起团游售后申请 */
export interface GroupTourRefundApplyParams {
  order_id: number;
  apply_reason: string;
  apply_desc: string;
  apply_img: string;
  refund_money: number;
}

/** 发起团游售后申请响应 data */
export interface GroupTourRefundApplyData {
  refund_id: number;
  refund_no: string;
  refund_status: number;
  status_text: string;
  refund_money: string;
}

/** 发起团游售后申请响应 */
export interface GroupTourRefundApplyResponse {
  code: number;
  message: string;
  data: GroupTourRefundApplyData;
}

/** 团游售后详情响应 */
export interface GroupTourRefundDetailResponse {
  code: number;
  message: string;
  data: RefundInfo;
}

/** 团游售后列表项 */
export interface GroupTourRefundListItem extends RefundInfo {
  order_id: number;
  order_no: string;
}

/** 团游售后列表请求参数 */
export interface GroupTourRefundListParams {
  page?: number;
  limit?: number;
  status?: number;
}

/** 团游售后列表响应 */
export interface GroupTourRefundListResponse {
  code: number;
  message: string;
  data: {
    list: GroupTourRefundListItem[];
    total: number;
    page: number;
    limit: number;
  };
}

/** 取消团游售后申请请求参数 */
export interface GroupTourRefundCancelParams {
  refund_id: number;
}

/** 取消团游售后申请响应 */
export interface GroupTourRefundCancelResponse {
  code: number;
  message: string;
  data: unknown;
}

/**
 * 发起团游售后申请
 * POST /home/group-tour-refund/apply
 */
export function applyGroupTourRefund(params: GroupTourRefundApplyParams) {
  return http.post<GroupTourRefundApplyResponse>("/home/group-tour-refund/apply", params);
}

/**
 * 获取团游售后详情
 * GET /home/group-tour-refund/detail
 */
export function getGroupTourRefundDetail(refundId: number) {
  return http.get<GroupTourRefundDetailResponse>("/home/group-tour-refund/detail", {
    params: { refund_id: refundId },
  });
}

/**
 * 获取团游售后列表
 * GET /home/group-tour-refund/list
 */
export function getGroupTourRefundList(params: GroupTourRefundListParams = {}) {
  return http.get<GroupTourRefundListResponse>("/home/group-tour-refund/list", { params });
}

/**
 * 取消团游售后申请
 * POST /home/group-tour-refund/cancel
 */
export function cancelGroupTourRefund(params: GroupTourRefundCancelParams) {
  return http.post<GroupTourRefundCancelResponse>("/home/group-tour-refund/cancel", params);
}
